import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import styles from './mention-extension.module.scss'

export interface MentionOptions {
    HTMLAttributes: Record<string, any>
    renderLabel: (props: { options: MentionOptions; node: any }) => string
    suggestion: any
    mentionClass?: string
    userMentionClass?: string
    modelMentionClass?: string
    onMentionClick?: (mentionId: string, event: Event, element: HTMLElement) => void
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        mention: {
            /**
             * Insert a mention
             */
            insertMention: (attributes: {
                id: string
                label?: string
                type?: string
                denotationChar?: string
            }) => ReturnType
        }
    }
}

export const MentionPluginKey = new PluginKey('mention')

export const KnovMention = Node.create<MentionOptions>({
    name: 'mention',

    addOptions() {
        return {
            HTMLAttributes: {},
            renderLabel({ options, node }) {
                const mention = node.attrs
                return `${mention.denotationChar || '@'}${mention.label || mention.id}`
            },
            suggestion: {
                char: '@',
                pluginKey: MentionPluginKey,
                command: ({ editor, range, props }) => {
                    // Remove the trigger character and any search text
                    editor.chain().focus().deleteRange(range).insertMention(props).run()
                },
            },
        }
    },

    group: 'inline',

    inline: true,

    selectable: false,

    atom: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-id'),
                renderHTML: attributes => ({
                    'data-mention-id': attributes.id,
                }),
            },
            label: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-label'),
                renderHTML: attributes => ({
                    'data-mention-label': attributes.label,
                }),
            },
            type: {
                default: 'user',
                parseHTML: element => element.getAttribute('data-mention-type'),
                renderHTML: attributes => ({
                    'data-mention-type': attributes.type,
                }),
            },
            denotationChar: {
                default: '@',
                parseHTML: element => element.getAttribute('data-mention-char'),
                renderHTML: attributes => ({
                    'data-mention-char': attributes.denotationChar,
                }),
            },
            content: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-content'),
                renderHTML: attributes => ({
                    'data-mention-content': attributes.content,
                }),
            },
            link: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-link'),
                renderHTML: attributes => ({
                    'data-mention-link': attributes.link,
                }),
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-mention-id]',
            },
        ]
    },

    renderHTML({ node, HTMLAttributes }) {
        const mention = node.attrs
        const isModel = mention.type === 'model' || mention.denotationChar === '@@'

        // Use CSS module classes from options, fallback to default strings
        const mentionClass = this.options.mentionClass || 'mention'
        const typeClass = isModel
            ? this.options.modelMentionClass || 'model-mention'
            : this.options.userMentionClass || 'user-mention'

        // Create click handler for user mentions
        const attrs: Record<string, any> = {
            class: `${mentionClass} ${typeClass}`,
            'data-mention-id': mention.id,
            'data-mention-type': mention.type || 'user',
            'data-mention-char': mention.denotationChar || '@',
            'data-mention-content': mention.content || mention.label || mention.id,
            'data-mention-link': mention.link,
        }

        // Add clickable class for user mentions only
        if (!isModel && this.options.onMentionClick) {
            attrs.class = `${attrs.class} ${styles.clickableMention}`
        }

        return [
            'span',
            mergeAttributes(attrs, this.options.HTMLAttributes, HTMLAttributes),
            this.options.renderLabel({
                options: this.options,
                node,
            }),
        ]
    },

    renderText({ node }) {
        const mention = node.attrs
        return `${mention.denotationChar || '@'}${mention.label || mention.content || mention.id}`
    },

    addCommands() {
        return {
            insertMention:
                attributes =>
                ({ chain }) => {
                    return chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs: attributes,
                        })
                        .run()
                },
        }
    },

    addProseMirrorPlugins() {
        const { onMentionClick } = this.options

        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),

            // Mention click handler plugin - using DOM events for better compatibility
            new Plugin({
                key: new PluginKey('mentionClickHandler'),

                view(editorView) {
                    const handleMentionClick = (event: Event) => {
                        const target = event.target as HTMLElement

                        // Check if clicked element or its parent is a mention span
                        let mentionElement = target
                        if (!mentionElement.hasAttribute('data-mention-id')) {
                            mentionElement = target.closest('[data-mention-id]') as HTMLElement
                        }

                        if (mentionElement && mentionElement.hasAttribute('data-mention-id')) {
                            const mentionId = mentionElement.getAttribute('data-mention-id')
                            const mentionType =
                                mentionElement.getAttribute('data-mention-type') || 'user'

                            // Only handle user mentions, not model mentions
                            if (mentionType !== 'model' && mentionId) {
                                event.preventDefault()
                                event.stopPropagation()

                                if (onMentionClick) {
                                    onMentionClick(mentionId, event, mentionElement)
                                }

                                return true
                            }
                        }

                        return false
                    }

                    // Add click listener to the editor DOM
                    const editorDOM = editorView.dom
                    editorDOM.addEventListener('click', handleMentionClick, true) // Use capture phase

                    return {
                        destroy() {
                            editorDOM.removeEventListener('click', handleMentionClick, true)
                        },
                    }
                },
            }),
        ]
    },
})
